import { useState } from 'react';
import { motion } from 'framer-motion';

export default function Newsletter() {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [message, setMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email) {
      setStatus('error');
      setMessage('Please enter your email address');
      return;
    }

    setStatus('loading');

    try {
      const formData = new URLSearchParams();
      formData.append('email', email);

      const response = await fetch('https://readdy.ai/api/public/form/submit/9396f31d77c954f423ce345f87cd25c5/newsletter', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formData.toString(),
      });

      if (response.ok) {
        setStatus('success');
        setMessage('Thank you for subscribing! Check your inbox for exclusive travel deals.');
        setEmail('');
      } else {
        setStatus('error');
        setMessage('Something went wrong. Please try again.');
      }
    } catch (error) {
      setStatus('error');
      setMessage('Failed to subscribe. Please try again later.');
    }
  };

  return (
    <section className="py-24 bg-gradient-to-r from-accent-pink via-accent-orange to-accent-yellow relative overflow-hidden">
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-0 left-0 w-96 h-96 bg-white rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-white rounded-full blur-3xl"></div>
      </div>

      <div className="relative max-w-3xl mx-auto px-6 lg:px-8 text-center">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <i className="ri-mail-line text-4xl text-white"></i>
          </div>

          <h2 className="text-4xl lg:text-5xl font-bold text-white mb-4">
            Never Miss an Adventure
          </h2>
          <p className="text-lg text-white/90 mb-8">
            Get exclusive deals, travel tips, and inspiration delivered to your inbox
          </p>

          <form onSubmit={handleSubmit} className="max-w-xl mx-auto" data-readdy-form id="newsletter-form">
            <div className="flex flex-col sm:flex-row gap-4">
              <input
                type="email"
                name="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email address"
                className="flex-1 h-14 px-6 rounded-full text-navy-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-white"
                disabled={status === 'loading'}
              />
              <button
                type="submit"
                disabled={status === 'loading'}
                className="h-14 px-8 bg-gradient-to-r from-primary-600 to-navy-900 text-white rounded-full font-semibold hover:shadow-xl transition-all disabled:opacity-50 whitespace-nowrap cursor-pointer"
              >
                {status === 'loading' ? 'Subscribing...' : 'Subscribe'}
              </button>
            </div>
          </form>

          {status === 'success' && (
            <motion.p
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-4 text-white font-medium"
            >
              {message}
            </motion.p>
          )}

          {status === 'error' && (
            <motion.p
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-4 text-white/90"
            >
              {message}
            </motion.p>
          )}

          <p className="text-sm text-white/70 mt-4">
            We respect your privacy. Unsubscribe at any time.{' '}
            <a href="#privacy" className="underline hover:text-white cursor-pointer">
              Privacy Policy
            </a>
          </p>
        </motion.div>
      </div>
    </section>
  );
}